import {AGENCY_KEYS} from "@/api/agencyApi";
import RefluencedLogo from "@/assets/refluenced_icon.svg?react";
import Button from "@/components/Common/Button";
import FileUpload from "@/components/Common/FileUpload";
import InputField from "@/components/Common/forms/InputField";
import Modal from "@/components/Common/Modal";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useSelectedAgency} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {useCreateAgencyStore} from "@/store/createAgency";
import useUserStore from "@/store/user";
import {useMutation} from "@tanstack/react-query";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const WelcomeToMultiAccountModal: FC = () => {
  const {t} = useTranslation(["multi_account", "common"]);
  const {refetch: refetchUser} = useFetchUser();
  const user = useUserStore(state => state.user);
  const agency = useSelectedAgency();
  const isTrialing = useMemo(
    () => !user.agencies?.[0]?.agency?.filled_out_agency_data && !user.package_subscription,
    [user.agencies, user.package_subscription],
  );
  const form = useCreateAgencyStore(state => state.form);
  const errors = useCreateAgencyStore(state => state.errors);
  const setValue = useCreateAgencyStore(state => state.setValue);
  const validate = useCreateAgencyStore(state => state.validateSetUpMultiAccountSchema);
  const [logo, setLogo] = useState<File | null>(null);

  const {mutateAsync: updateAgency, isPending: isUpdatingAgency} = useMutation({
    mutationKey: AGENCY_KEYS.mAgency(agency?.id!),
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("name", form.agency_name ?? "");
      formData.append("address", form.address ?? "");
      formData.append("city", form.city ?? "");
      formData.append("postal_code", form.postal_code ?? "");
      formData.append("website", form.website ?? "");

      if (logo) {
        formData.append("picture", logo);
        formData.append("picture320", logo);
      }

      return await client.patch(AGENCY_KEYS.mAgency(agency?.id!, isTrialing ? "?start_trial=true" : "")[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: () => {
      refetchUser();
    },
  });

  return (
    <Modal
      onClose={() => void 0}
      shouldHideCloseButton
      customTitleRow={
        <div className="flex grow justify-center p-5">
          <span className="text-neutral-black text-xl font-semibold">{t("welcome_to_multiaccount_title")}</span>
        </div>
      }
      customBottomRow={<></>}
      backgroundClassName="max-w-[800px]">
      <div className="text-neutral-black flex flex-col gap-2.5">
        <div className="flex flex-col items-center gap-5">
          <span className="text-xl font-semibold">
            {t(
              user.sub_brands?.length === 1
                ? "welcome_to_multiaccount_details_simple_upgrade"
                : "welcome_to_multiaccount_details_merge",
            )}
          </span>
          {isTrialing && (
            <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-xl font-semibold text-transparent">
              {t("welcome_to_multiaccount_details_trial")}
            </span>
          )}
          <RefluencedLogo className="size-[70px] shrink-0" />
        </div>
        <div className="flex flex-col items-center gap-5">
          <span className="text-neutral-black text-xl font-semibold">
            {t(agency?.large_brand ? "add_your_large_brands_logo_here" : "add_your_agencies_logo_here")}
          </span>
          <FileUpload file={logo} onUpload={files => setLogo(files[0])} />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <InputField
          label={t(agency?.large_brand ? "company_name" : "agency_name")}
          placeholder={t(agency?.large_brand ? "company_name" : "the_agency_name")}
          value={form.agency_name}
          onChange={value => setValue("form.agency_name", value)}
          error={errors?.agency_name?.[0]}
        />
        <div className="flex w-full flex-col gap-2">
          <InputField
            label={t(agency?.large_brand ? "large_brand_address" : "agency_address")}
            placeholder={t("street_and_number")}
            value={form.address}
            onChange={value => setValue("form.address", value)}
            error={errors?.address?.[0]}
          />
          <div className="flex gap-2">
            <InputField
              placeholder={t("postal_code")}
              value={form.postal_code}
              onChange={value => setValue("form.postal_code", value)}
              error={errors?.postal_code?.[0]}
            />
            <InputField
              placeholder={t("city")}
              value={form.city}
              onChange={value => setValue("form.city", value)}
              error={errors?.city?.[0]}
            />
          </div>
        </div>
        <InputField
          label={t(agency?.large_brand ? "large_brand_website" : "agency_website")}
          placeholder={t(agency?.large_brand ? "the_large_brand_website" : "the_agency_website")}
          value={form.website}
          onChange={value => setValue("form.website", value)}
          error={errors?.website?.[0]}
        />
        <Button
          onClick={async () => {
            if (validate()) {
              await updateAgency();
            }
          }}
          isDisabled={isUpdatingAgency}
          isLoading={isUpdatingAgency}
          buttonStyle="Blue"
          className="self-center px-8">
          {t("continue_to_account")}
        </Button>
      </div>
    </Modal>
  );
};

export default WelcomeToMultiAccountModal;
